import { useCallback, useEffect, type ReactNode } from 'react';
import {
  View,
  Text,
  Pressable,
  StyleSheet,
  Dimensions,
  Platform,
  ScrollView,
  useWindowDimensions,
} from 'react-native';
import { useAppStore } from '../store/appStore';
import { useTheme } from '../styles/theme';
import { centerScrollViewRef, centerScrollOffset } from '../services/events';

interface DrawerLayoutProps {
  leftContent: ReactNode;
  rightContent: ReactNode;
  children: ReactNode;
  overlay?: ReactNode;
}

const DRAWER_WIDTH = 280;
const DESKTOP_BREAKPOINT = 768;
const APP_VERSION = '5.0.0';

export default function DrawerLayout({
  leftContent,
  rightContent,
  children,
  overlay,
}: DrawerLayoutProps) {
  const leftOpen = useAppStore((s) => s.leftDrawerOpen);
  const rightOpen = useAppStore((s) => s.rightDrawerOpen);
  const closeDrawers = useAppStore((s) => s.closeDrawers);
  const toggleLeftDrawer = useAppStore((s) => s.toggleLeftDrawer);
  const toggleRightDrawer = useAppStore((s) => s.toggleRightDrawer);
  const viewMode = useAppStore((s) => s.settings.view_mode.value);
  const isMobileAgent = useAppStore((s) => s.isMobileAgent);

  const { width } = useWindowDimensions();
  const { colors } = useTheme();
  const isDesktop = width > DESKTOP_BREAKPOINT;
  const isMushaf = viewMode === 'mushaf';

  // Swipe handling for mobile (web touch events)
  useEffect(() => {
    if (Platform.OS !== 'web' || !isMobileAgent) return;

    const centerPane = document.getElementById('center-pane');
    if (!centerPane) return;

    let startX = 0;
    let startY = 0;
    let lastX = 0;
    let dragging = false;
    let fromEdge: 'left' | 'right' | null = null;

    const EDGE_THRESHOLD = 24;
    const MIN_DRAG_DISTANCE = 60;

    const handleTouchStart = (e: TouchEvent) => {
      if (e.touches.length !== 1) return;
      const touch = e.touches[0]!;
      startX = touch.clientX;
      startY = touch.clientY;
      lastX = startX;
      dragging = false;
      fromEdge = null;

      const w = Dimensions.get('window').width;
      const state = useAppStore.getState();

      if (!state.leftDrawerOpen && !state.rightDrawerOpen) {
        if (startX <= EDGE_THRESHOLD) fromEdge = 'left';
        else if (startX >= w - EDGE_THRESHOLD) fromEdge = 'right';
      } else {
        fromEdge = state.leftDrawerOpen ? 'left' : 'right';
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (e.touches.length !== 1 || !fromEdge) return;
      const touch = e.touches[0]!;
      const dx = touch.clientX - startX;
      const dy = touch.clientY - startY;
      lastX = touch.clientX;

      if (!dragging) {
        if (Math.abs(dx) > 10 && Math.abs(dx) > Math.abs(dy)) {
          dragging = true;
        } else {
          return;
        }
      }
      if (dragging) e.preventDefault();
    };

    const handleTouchEnd = () => {
      if (!fromEdge) return;
      const dx = lastX - startX;
      const state = useAppStore.getState();

      if (!state.leftDrawerOpen && !state.rightDrawerOpen) {
        if (fromEdge === 'left' && dx > MIN_DRAG_DISTANCE) toggleLeftDrawer();
        else if (fromEdge === 'right' && dx < -MIN_DRAG_DISTANCE) toggleRightDrawer();
      } else if (state.leftDrawerOpen && fromEdge === 'left' && dx < -MIN_DRAG_DISTANCE) {
        closeDrawers();
      } else if (state.rightDrawerOpen && fromEdge === 'right' && dx > MIN_DRAG_DISTANCE) {
        closeDrawers();
      }
      dragging = false;
      fromEdge = null;
    };

    centerPane.addEventListener('touchstart', handleTouchStart, { passive: true });
    centerPane.addEventListener('touchmove', handleTouchMove, { passive: false });
    centerPane.addEventListener('touchend', handleTouchEnd);
    centerPane.addEventListener('touchcancel', handleTouchEnd);
    return () => {
      centerPane.removeEventListener('touchstart', handleTouchStart);
      centerPane.removeEventListener('touchmove', handleTouchMove);
      centerPane.removeEventListener('touchend', handleTouchEnd);
      centerPane.removeEventListener('touchcancel', handleTouchEnd);
    };
  }, [isMobileAgent, toggleLeftDrawer, toggleRightDrawer, closeDrawers]);

  const handleOverlayPress = useCallback(() => {
    closeDrawers();
  }, [closeDrawers]);

  // On desktop the left pane is permanently visible; overlay only for right drawer on mobile
  const showOverlay = isDesktop ? rightOpen : (leftOpen || rightOpen);

  return (
    <View nativeID="viewport" style={styles.viewport}>
      {/* Center pane — offset on desktop to make room for the permanent left pane */}
      <View
        nativeID="center-pane"
        style={[
          styles.centerPane,
          { backgroundColor: isMushaf ? colors.cardBg : colors.centerPaneBg },
          isDesktop && { marginLeft: DRAWER_WIDTH } as any,
        ]}
      >
        <ScrollView
          ref={(r: any) => { centerScrollViewRef.current = r; }}
          nativeID="scroll-center-pane-helper"
          style={[styles.scrollHelper, { backgroundColor: isMushaf ? colors.cardBg : colors.centerPaneBg }]}
          contentContainerStyle={styles.scrollContent}
          onScroll={(e: any) => { centerScrollOffset.current = e.nativeEvent.contentOffset.y; }}
          scrollEventThrottle={16}
        >
          <View nativeID="scroll-center-pane" style={styles.mainContent}>
            {children}
          </View>
          <View style={[styles.footer, { backgroundColor: isMushaf ? colors.cardBg : colors.footerBg, borderTopColor: isMushaf ? 'transparent' : colors.footerBorderColor }]}>
            <Text style={[styles.footerText, { color: colors.footerText }]}>
              © Guided Geeks {new Date().getFullYear()} - v{APP_VERSION}
            </Text>
          </View>
        </ScrollView>
      </View>

      {/* Overlay shadow when a mobile drawer (or right drawer) is open */}
      {showOverlay && (
        <Pressable
          nativeID="shadow"
          style={[styles.overlay, { backgroundColor: colors.overlayBg }]}
          onPress={handleOverlayPress}
        />
      )}

      {/* Left pane — always visible on desktop, drawer overlay on mobile */}
      {(isDesktop || leftOpen) && (
        <View
          nativeID="left-pane"
          style={[
            styles.drawer,
            styles.leftDrawer,
            { backgroundColor: colors.leftPaneBg },
            isDesktop ? styles.leftDrawerDesktop : styles.leftDrawerMobile,
          ]}
        >
          {leftContent}
        </View>
      )}

      {/* Right drawer */}
      {rightOpen && (
        <View nativeID="right-pane" style={[styles.drawer, styles.rightDrawer, { backgroundColor: colors.rightPaneBg }]}>
          <ScrollView
            nativeID="scroll-right-pane"
            style={styles.drawerScroll}
            contentContainerStyle={styles.drawerScrollContent}
          >
            {rightContent}
          </ScrollView>
        </View>
      )}

      {/* Overlay slot — rendered inside viewport so it shares the same stacking context as the drawers */}
      {overlay}
    </View>
  );
}

const styles = StyleSheet.create({
  viewport: {
    flex: 1,
  },
  centerPane: {
    flex: 1,
  },
  scrollHelper: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
    paddingTop: 50,
  },
  mainContent: {
    flex: 1,
    paddingBottom: 20,
  },
  footer: {
    alignItems: 'center',
    paddingVertical: 20,
    borderTopWidth: 1,
  },
  footerText: {
    fontSize: 12,
  },
  overlay: {
    position: 'absolute' as any,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 1000,
  },
  drawer: {
    position: 'absolute' as any,
    top: 0,
    bottom: 0,
    width: DRAWER_WIDTH,
    zIndex: 1200,
    boxShadow: '2px 0 8px rgba(0,0,0,0.15)',
  },
  leftDrawer: {
    left: 0,
  },
  leftDrawerMobile: {
    top: 50, // start below the TopBar so it never covers the back button
  },
  leftDrawerDesktop: {
    // On desktop the left pane is part of the layout flow — no overlay shadow needed
    boxShadow: 'none',
    zIndex: 10,
  },
  rightDrawer: {
    right: 0,
    boxShadow: '-2px 0 8px rgba(0,0,0,0.3)',
  },
  drawerScroll: {
    flex: 1,
  },
  drawerScrollContent: {
    paddingTop: 50, // clear the TopBar
  },
});
